import { type JSX, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  wegregelEntfernen,
  wegregelSetzen,
  type Buchungsweg,
  type Halteart,
  type Kapazitaetszusammensetzung,
  type Wegstand,
} from '../api/termine';
import { Feld } from '../komponenten/Feld';
import { Meldung } from '../komponenten/Meldung';

/**
 * DIE KAPAZITÄT DES TERMINS (KAP-20) UND DAS WEBKONTINGENT (BUC-71).
 *
 * ============================================================================
 * DER SATZ, AUS DEM DAS FELD ENTSTAND
 * ============================================================================
 *
 * Auftraggeber, 27.09.2026: „Für einen Termin mit Einzelteilnehmern soll im
 * Termin festlegbar sein, wie viele Plätze darüber per Web buchbar sein
 * sollen."
 *
 * Das ist die OBERGRENZE nach BUC-71: Das Portal nimmt höchstens so viele
 * Plätze, der Innendienst sieht weiter alle. Ein leeres Feld heißt: keine
 * Portalgrenze. Die Reservierung — Plätze, die den anderen Wegen entzogen
 * sind — kennt die Schnittstelle auch; eine so gesetzte Regel behält ihre Art,
 * wenn hier nur die Zahl geändert wird.
 *
 * ============================================================================
 * DIE MASKE RECHNET NICHTS
 * ============================================================================
 *
 * KAP-20: Die Aufstellung ist die einzige gültige Quelle. Jede Zahl hier kommt
 * aus der API, die mit `@crm/kalkulation` rechnet — dieselbe Rechnung, gegen
 * die Portal und Innendienst buchen.
 */

const BEGRENZUNG: Readonly<Record<Kapazitaetszusammensetzung['begrenzendeGroesse'], string>> = {
  hoechstteilnehmer: 'Höchstteilnehmerzahl',
  ressourcen: 'Ressourcen',
  tagesplan: 'Tagesplan',
};

const HALTEART: Readonly<Record<Halteart, string>> = {
  buchung: 'Buchungen',
  reservierung: 'Reservierungen',
  freigabe_zahlung: 'Freigabe oder Zahlung',
  option: 'Optionen',
  angebotsvormerkung: 'Angebotsvormerkungen',
};

const WEG: Readonly<Record<Buchungsweg, string>> = {
  portal: 'Portal',
  innendienst: 'Innendienst',
  angebot: 'Angebot',
};

function regeltext(stand: Wegstand): string {
  if (stand.regel === null) return 'keine Grenze';
  const text =
    stand.regel.art === 'obergrenze'
      ? `höchstens ${String(stand.regel.plaetze)}`
      : `${String(stand.regel.plaetze)} reserviert`;
  return stand.regelWirksam ? text : `${text} (abgelaufen)`;
}

export function Kapazitaetskarte({
  termin,
  terminart,
  kapazitaet,
  darfAendern,
  beiGeaendert,
}: {
  readonly termin: string;
  readonly terminart: 'offen' | 'exklusiv';
  readonly kapazitaet: Kapazitaetszusammensetzung;
  /** `termin/aendern` — dasselbe Recht wie die übrige Kapazität am Termin. */
  readonly darfAendern: boolean;
  readonly beiGeaendert: () => void;
}): JSX.Element {
  const portal = kapazitaet.wege.find((w) => w.weg === 'portal');
  const [offen, setzeOffen] = useState(false);
  const [plaetze, setzePlaetze] = useState(String(portal?.regel?.plaetze ?? ''));
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const gehalten = (Object.keys(HALTEART) as Halteart[]).filter(
    (art) => kapazitaet.plaetzeJeHalteart[art] > 0,
  );

  const speichern = (): void => {
    const eingabe = plaetze.trim();
    // Leer heißt keine Portalgrenze. Gab es keine, gibt es nichts zu tun.
    const auftrag =
      eingabe === ''
        ? portal?.regel == null
          ? Promise.resolve()
          : wegregelEntfernen(termin, 'portal')
        : wegregelSetzen(termin, 'portal', {
            plaetze: Number(eingabe),
            art: portal?.regel?.art ?? 'obergrenze',
          });
    setzeLaeuft(true);
    auftrag
      .then(() => {
        setzeOffen(false);
        setzeFehler(null);
        beiGeaendert();
      })
      .catch((u: unknown) => {
        setzeFehler(u as ApiFehler);
      })
      .finally(() => setzeLaeuft(false));
  };

  const ungueltig = plaetze.trim() !== '' && !/^\d+$/.test(plaetze.trim());

  return (
    <>
      <h3>Kapazität</h3>
      <dl className="angaben">
        <dt>Maßgeblich</dt>
        <dd>
          {kapazitaet.massgeblicheKapazitaet} Plätze{' '}
          <span className="hilfe">· begrenzt durch {BEGRENZUNG[kapazitaet.begrenzendeGroesse]}</span>
        </dd>
        <dt>Gehalten</dt>
        <dd>
          {gehalten.length === 0
            ? 'nichts'
            : gehalten
                .map((art) => `${String(kapazitaet.plaetzeJeHalteart[art])} ${HALTEART[art]}`)
                .join(' · ')}
        </dd>
        {kapazitaet.kontingentGesamt > 0 && (
          <>
            <dt>Kontingente</dt>
            <dd>{kapazitaet.kontingentGesamt} Plätze für Kunden oder Kundengruppen</dd>
          </>
        )}
        <dt>Frei</dt>
        <dd>
          {kapazitaet.freiAnonym}
          {/* KAP-22 */}
          <span className="hilfe"> · ohne feststehenden Kunden</span>
          {/* ÄA-41: Die Überbelegung ist ein Konflikt und steht in der Achtungsfarbe. */}
          {kapazitaet.ueberbelegt && <span className="marke marke--warn">überbelegt</span>}
        </dd>
      </dl>

      {terminart === 'offen' && (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="hilfe">Je Buchungsweg</caption>
            <thead>
              <tr>
                <th scope="col">Weg</th>
                <th scope="col">Kontingent</th>
                <th scope="col">gehalten</th>
                <th scope="col">frei</th>
              </tr>
            </thead>
            <tbody>
              {kapazitaet.wege.map((w) => (
                <tr key={w.weg}>
                  <td>{WEG[w.weg]}</td>
                  <td>{regeltext(w)}</td>
                  <td>{w.gehalten}</td>
                  <td>
                    {w.frei}
                    {w.begrenzenderWeg === 'obergrenze' && (
                      <span className="hilfe"> · Grenze erreicht</span>
                    )}
                    {w.begrenzenderWeg === 'fremde_reservierung' && (
                      <span className="hilfe"> · Plätze anderer Wege reserviert</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fehler !== null && <Meldung fehler={fehler} />}

      {terminart === 'offen' && darfAendern && !offen && (
        <button type="button" className="knopf" onClick={() => setzeOffen(true)}>
          {portal?.regel == null ? 'Webkontingent festlegen' : 'Webkontingent ändern'}
        </button>
      )}

      {terminart === 'offen' && darfAendern && offen && (
        <form
          className="unterblock"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!ungueltig) speichern();
          }}
        >
          <Feld
            beschriftung="Über das Portal buchbar (Plätze)"
            wert={plaetze}
            aendern={setzePlaetze}
            eingabeart="numeric"
            hilfe="Höchstens so viele Plätze sind über das Web buchbar; der Innendienst bucht weiter alle freien. Leer lassen heißt: keine Portalgrenze."
            verstoesse={
              ungueltig
                ? [
                    {
                      kennung: 'keine_zahl',
                      feld: 'plaetze',
                      meldung: {
                        wasGeschah: 'Das ist keine ganze Zahl.',
                        auswirkung: 'Das Webkontingent wurde nicht gespeichert.',
                        naechsterSchritt: 'Geben Sie eine Platzzahl ein oder lassen Sie das Feld leer.',
                      },
                    },
                  ]
                : (fehler?.alleZumFeld('plaetze') ?? [])
            }
          />
          <button type="submit" className="knopf knopf--haupt" disabled={laeuft || ungueltig}>
            Speichern
          </button>{' '}
          <button
            type="button"
            className="knopf"
            onClick={() => {
              setzeOffen(false);
              setzeFehler(null);
            }}
          >
            Abbrechen
          </button>
        </form>
      )}
    </>
  );
}
